<template>
  <div style="min-height: calc(100vh - 158px);" class="card">
    <div style="background: #DBE7F2" class="card p-2">
      <TitleButton
        :showBtn="false"
        :showAddNew="false"
        title="Item Sales Report"
      />
      <DateQuerySetter
        btnTitle="Go"
        @onClickGo="getReport"
      />
    </div>

    <div class="d-flex justify-content-end px-2 mt-1">
      <button
          @click="exportReport"
          class="btn btn-outline-secondary btn-sm btn-download-invoice waves-effect me-1"
      >
        Export
      </button>
      <button
          @click="goToPrint"
          class="btn btn-outline-secondary btn-sm btn-download-invoice waves-effect me-1"
      >
        Print
      </button>
    </div>

    <div class="px-2 mt-1">
        <div v-for="invoice in reportData" :key="invoice.id" class="mb-2 border rounded p-1">
          <div class="d-flex gap-4 mb-1">
            <span><strong>Bill No:</strong> {{ invoice.bill_number }}</span>
            <span><strong>Bill Date:</strong> {{ invoice.date }}</span>
            <span><strong>Customer:</strong> {{ invoice.contact_profile_name }}</span>
          </div>

          <table class="table table-bordered table-sm mb-1">
            <thead style="background: #f5f5a0;">
              <tr>
                <th>Item Name</th>
                <th>Unit</th>
                <th class="text-right">Qty</th>
                <th class="text-right">Price</th>
                <th class="text-right">Amount</th>
                <th class="text-right">VAT Amount</th>
                <th class="text-right">Total Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in invoice.general" :key="idx">
                <td>{{ item.product_name }}</td>
                <td>{{ item.unit_name }}</td>
                <td class="text-right">{{ item.quantity }}</td>
                <td class="text-right">{{ item.rate }}</td>
                <td class="text-right">{{ item.amount }}</td>
                <td class="text-right">{{ item.vat_amount }}</td>
                <td class="text-right">{{ item.total_amount }}</td>
              </tr>
            </tbody>
          </table>

          <div class="d-flex gap-4 flex-wrap small">
            <span><strong>Total Bill Amount:</strong> {{ invoice.subtotal_amount }}</span>
            <span><strong>Total VAT Amount:</strong> {{ invoice.vat_amount }}</span>
            <span><strong>Total Discount:</strong> {{ invoice.total_discount }}</span>
            <span><strong>Total Point Redeemed:</strong> {{ invoice.points_redeemed }}</span>
            <span><strong>Net Bill Amount:</strong> {{ invoice.total_amount }}</span>
            <span><strong>Total Paid:</strong> {{ invoice.paid_amount }}</span>
            <span><strong>Total Due:</strong> {{ (invoice.total_amount - invoice.paid_amount - invoice.adjusted_amount).toFixed(2) }}</span>
          </div>
        </div> 
    </div>

    <div class="px-2">
      <Pagination :offset="offset" @onPageChange="onPageChange" ref="pagination"/>
    </div>

    <Loader v-if="loading" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import {useRoute, useRouter} from 'vue-router'
import TitleButton from '@/components/atom/TitleButton'
import DateQuerySetter from '@/components/atom/DateQuerySetter'
import Loader from '@/components/atom/LoaderComponent'
import handleInventory from '@/services/modules/inventory'
import Pagination from '@/components/atom/Pagination'
import {useStore} from "vuex";
import handleCompany from '@/services/modules/company'
import handleBarcode from "@/services/modules/barcode";
import pdfPrinter from '@/services/utils/pdf/itemSalesReportPdf'

const $route = useRoute()
const $router = useRouter()
const $store = useStore()
const { fetchItemSalesReport } = handleInventory()
const { fetchCompanyInfo } = handleCompany()
const { exportToPDF } = pdfPrinter()
const { fetchBarcode } = handleBarcode();

const loading = ref(false)
const reportData = ref([])
const companyInfo = ref({})
const offset = ref(20);

const companyId = computed(() => $route.params.companyId)
const start = computed(() => $route.query.start)
const end = computed(() => $route.query.end)
const page = computed(() => $route.query.page);

const getQuery = () => {
  let query = `?company_id=${companyId.value}`
  if (start.value) query += `&start_date=${start.value}`
  if (end.value) query += `&end_date=${end.value}`
  if (page.value) query += '&page=' + page.value
  query += '&offset=' + offset.value
  return query
}

const getReport = async () => {
  loading.value = true
  reportData.value = []

  try {
    const res = await fetchItemSalesReport(getQuery())

    if (!res.status) {
      resetPagination()
      return
    }

    reportData.value = res.data.data
    await setPagination(res.data)

  } catch (error) {
  } finally {
    loading.value = false
  }
}

const exportReport = () => {
  const token = localStorage.getItem('token')
  let query = `?company_id=${companyId.value}`
  if (start.value) query += `&start_date=${start.value}`
  if (end.value) query += `&end_date=${end.value}`
  const url = `${process.env.VUE_APP_BASE_URL}/export/hospital/item-sales-report${query}&_token=${token}&is_export=1`
  const a = document.createElement('a')
  a.setAttribute('href', url)
  a.click()
}

const goToPrint = async () => {
  const userName = $store.state.user.user.name;
  loading.value = true
  try {
    let query = getQuery()
    query += '&is_all=1'

    const res = await fetchItemSalesReport(query)
    if (!res.status) return

    const qrcodeData = JSON.stringify({
      created_by: userName,
      created_date_time: new Date().toISOString()
    });
    const barcodeRes = await fetchBarcode(`?code=item-sale-report&qrcode=${qrcodeData}`);
    exportToPDF(companyInfo.value, res.data, start.value, end.value, barcodeRes.qrcode)
  } catch (error) {
  } finally {
    loading.value = false
  }
}

const onPageChange = async (page) => {
  let routeQuery = Object.assign({}, $route.query)
  routeQuery.page = page
  await $router.push({path: $route.path, query: routeQuery})
  await getReport();
}

const setPaginationData = (data) => {
  $store.commit('setPaginationData', data)
};

const resetPagination = () => {
  $store.commit('resetPagination')
};

const setPagination = (data) => {
  setPaginationData({
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page,
  })
};

onMounted(async () => {
  const companyQuery = `?company_id=${companyId.value}`
  const res = await fetchCompanyInfo(companyQuery)
  if (res.status) companyInfo.value = res.data
  getReport()
})
</script>
